import type { FC } from 'react';
import { Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiContent from 'src/core/models/api/ApiContent';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Styles from 'src/core/utils/Styles';
import Translations from 'src/core/utils/Translations';
import { useExerciseStore } from 'src/domain/stores/useExerciseStore';
import CardPattern from 'src/presentation/components/atoms/CardPattern';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { exerciseOpensInDialog } from 'src/core/utils/Exercise';
import { exerciseMetaLine } from 'src/presentation/helpers/ExerciseMeta';

export type ExerciseCardVariant = 'dark' | 'light';

const palettes = {
	dark: {
		surface: Colors.green900,
		pattern: Colors.white,
		label: Colors.white,
		title: Colors.white,
		meta: Colors.white,
	},
	light: {
		surface: Colors.white,
		pattern: null,
		label: Colors.gray550,
		title: Colors.gray800,
		meta: Colors.gray550,
	},
} as const;

interface Props {
	content: ApiContent | null;
	variant: ExerciseCardVariant;
	style?: StyleProp<ViewStyle>;
}

const ExerciseOfTheDayCard: FC<Props> = ({ content, variant, style }: Props) => {
	const { open } = useExerciseStore();

	if (content === null) {
		return null;
	}

	const palette = palettes[variant];
	const meta = exerciseMetaLine(content);

	return (
		<Pressable
			accessibilityLabel={`${Translations.exerciseOfTheDay}: ${content.title}`}
			accessibilityRole="button"
			style={[styles.card, Styles.card.shadowSoft, { backgroundColor: palette.surface }, style]}
			onPress={() => open(content)}
		>
			{palette.pattern === null ? null : (
				<View style={styles.patternClip}>
					<CardPattern color={palette.pattern} />
				</View>
			)}
			<View style={styles.thumbnail}>
				<ThermenImage
					contentFit="cover"
					source={content.media ? { uri: content.media } : null}
					style={styles.thumbnailImage}
				/>
				{exerciseOpensInDialog(content) ? (
					<View style={styles.play}>
						<SvgIcon name={SvgIconType.play} size={Dimens.iconSize8} tintColor={Colors.green900} />
					</View>
				) : null}
			</View>
			<View style={styles.text}>
				<Text style={[styles.label, { color: palette.label }]}>{Translations.exerciseOfTheDay}</Text>
				<Text style={[styles.title, { color: palette.title }]}>{content.title}</Text>
				{meta === null ? null : <Text style={[styles.meta, { color: palette.meta }]}>{meta}</Text>}
			</View>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	card: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: Dimens.gap14,
		padding: Dimens.padding20,
		borderRadius: Dimens.borderRadius4,
	},
	patternClip: {
		...StyleSheet.absoluteFill,
		borderRadius: Dimens.borderRadius4,
		overflow: 'hidden',
	},
	thumbnail: {
		backgroundColor: Colors.gray300,
		width: Dimens.size88,
		height: Dimens.size88,
		borderRadius: Dimens.borderRadius4,
		overflow: 'hidden',
	},
	thumbnailImage: {
		width: '100%',
		height: '100%',
	},
	play: {
		position: 'absolute',
		right: Dimens.padding6,
		bottom: Dimens.padding6,
		width: Dimens.size20,
		height: Dimens.size20,
		borderRadius: Dimens.borderRadiusPill,
		backgroundColor: Colors.white,
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: Dimens.padding2,
	},
	text: {
		flex: Dimens.flex1,
		gap: Dimens.gap4,
	},
	label: {
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize12,
		lineHeight: Dimens.lineHeight16,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight20,
	},
	meta: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize12,
		lineHeight: Dimens.lineHeight21,
	},
});

export default ExerciseOfTheDayCard;
